import { Field } from '@campfire/field';
import { CampfireRichEditor } from '@campfire/campfire-rich-editor';
import { TabletButton } from '@campfire/tablet-button';
import classNames from 'classnames';
import { Grid, Switch, Typography, Box } from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/styles';
import { EditorState, convertToRaw } from 'draft-js';
import { Form, Formik } from 'formik';
import { isEqual } from 'lodash';
import React, { memo, useMemo, useState } from 'react';
import { FormSectionV2 } from '../../../../../common/FormSectionV2';
import { useProgramManagementContext } from '../../ProgramManagementContext';
import { ProgramManagementSingleProgramBasicInfo } from '../../__generated__/ProgramManagementSingleProgramBasicInfo';
import { convertToEditorState } from '../../../../general/activities-v2/AllPrograms/AllProgramsHelpers';
import { ConfirmationDialog } from '../../../../../common/dialogs/ConfirmationDialog';
import { useProgramsContext } from '../../../../../global/programs-sell/useProgramsContext';
import { useEnterpriseLock } from '../../../../../global/auth/use-enterprise-lock/use-enterprise-lock';

export const useStyles = makeStyles(() =>
  createStyles({
    switchText: {
      fontSize: '0.875rem',
      color: '#000000',
      opacity: 0.6,
    },
    switchHeading: {
      fontWeight: 700,
      fontSize: '0.75rem',
    },
    switchBody: {
      fontWeight: 400,
    },
  })
);

export const ProgramBasicInfoSectionForm = memo(
  ({ programBasicInfo }: { programBasicInfo: ProgramManagementSingleProgramBasicInfo | undefined }) => {
    const [showConfirmationDialog, setShowConfirmationDialog] = useState<boolean>(false);
    const [showAutoEnrolDialog, setShowAutoEnrolDialog] = useState<boolean>(false);
    const { runSaveProgram, saveProgramIsLoading } = useProgramManagementContext();
    const { refetchProgramsContext } = useProgramsContext();
    const classes = useStyles();
    const locked = useEnterpriseLock();

    const initialValues = useMemo(
      () => ({
        programName: programBasicInfo?.name ?? '',
        programDescription: programBasicInfo?.description
          ? convertToEditorState(programBasicInfo.description)
          : EditorState.createEmpty(),
        isHidden: programBasicInfo?.isHidden ?? false,
        isRestrictedProgram: programBasicInfo?.isRestrictedProgram ?? false,
        autoEnrolAllVolunteers: programBasicInfo?.autoEnrolAllVolunteers ?? false,
      }),
      [programBasicInfo]
    );

    return (
      <FormSectionV2
        title='Basic Info'
        description={
          <Typography variant='body2' display='block' color='textSecondary'>
            Give your Program a unique, memorable name. Add a description to help Volunteers and other Managers to
            understand the purpose of the Program.
          </Typography>
        }
      >
        <Formik
          enableReinitialize
          initialValues={initialValues}
          onSubmit={(vals) => {
            runSaveProgram(
              {
                name: vals.programName,
                description: JSON.stringify(convertToRaw(vals.programDescription.getCurrentContent())),
                programId: programBasicInfo?.programId,
                active: programBasicInfo?.dateSuspended === null,
                isHidden: vals.isHidden,
                isRestrictedProgram: vals.isRestrictedProgram,
                autoEnrolAllVolunteers: vals.autoEnrolAllVolunteers,
              },
              refetchProgramsContext
            );
          }}
        >
          {({ handleReset, values, setFieldValue }) => {
            const fieldsUnchanged: boolean = useMemo(
              () =>
                values.programName === initialValues.programName &&
                isEqual(
                  convertToRaw(values.programDescription.getCurrentContent()),
                  convertToRaw(initialValues.programDescription.getCurrentContent())
                ) &&
                values.isHidden === initialValues.isHidden &&
                values.isRestrictedProgram === initialValues.isRestrictedProgram &&
                values.autoEnrolAllVolunteers === initialValues.autoEnrolAllVolunteers,
              [
                values.programName,
                values.programDescription,
                values.isHidden,
                values.isRestrictedProgram,
                values.autoEnrolAllVolunteers,
              ]
            );
            return (
              <Form>
                <Grid container direction='column' spacing={2}>
                  <Grid item xs={12}>
                    <Field
                      required
                      name='programName'
                      label='Program name'
                      variant='outlined'
                      helper
                      fullWidth
                      disabled={locked}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <CampfireRichEditor
                      placeholder='Description (Optional)'
                      editorState={values.programDescription}
                      readOnly={locked}
                      setEditorState={(eState) => {
                        setFieldValue('programDescription', eState);
                      }}
                    />
                  </Grid>
                  <Grid item container xs={12} spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Box display='flex' alignItems='flex-start'>
                        <Switch
                          checked={values.isRestrictedProgram}
                          onChange={() => {
                            if (values.isRestrictedProgram) {
                              setShowConfirmationDialog(true);
                              return;
                            }
                            setFieldValue('isRestrictedProgram', !values.isRestrictedProgram);
                          }}
                          color='primary'
                          disabled={locked}
                          name='pinned'
                          inputProps={{ 'aria-label': 'program hidden switch' }}
                        />
                        <Box ml={0.4}>
                          <Typography className={classNames(classes.switchText, classes.switchHeading)}>
                            VOLUNTEERS MUST APPLY
                          </Typography>
                          <Typography className={classNames(classes.switchText, classes.switchBody)}>
                            Volunteers will be unable to automatically join this program, and instead must apply and be
                            approved by a program manager or admin
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box display='flex' alignItems='flex-start'>
                        <Switch
                          checked={values.isHidden}
                          onChange={() => setFieldValue('isHidden', !values.isHidden)}
                          color='primary'
                          disabled={locked}
                          name='pinned'
                          inputProps={{ 'aria-label': 'program restriction switch' }}
                        />
                        <Box ml={0.4}>
                          <Typography className={classNames(classes.switchText, classes.switchHeading)}>
                            HIDDEN PROGRAM
                          </Typography>
                          <Typography className={classNames(classes.switchText, classes.switchBody)}>
                            Volunteers who are not part of this program will be unable to see or join it
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box display='flex' alignItems='flex-start'>
                        <Switch
                          checked={values.autoEnrolAllVolunteers}
                          onChange={() => {
                            if (!values.autoEnrolAllVolunteers) {
                              setShowAutoEnrolDialog(true);
                              return;
                            }
                            setFieldValue('autoEnrolAllVolunteers', false);
                          }}
                          color='primary'
                          disabled={locked}
                          name='autoEnrolAllVolunteers'
                          inputProps={{ 'aria-label': 'auto enrol all volunteers switch' }}
                        />
                        <Box ml={0.4}>
                          <Typography className={classNames(classes.switchText, classes.switchHeading)}>
                            AUTO-ENROL ALL VOLUNTEERS
                          </Typography>
                          <Typography className={classNames(classes.switchText, classes.switchBody)}>
                            Every volunteer is automatically enrolled in this program, including new volunteers as they
                            join
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                  {!locked && (
                    <Grid item container spacing={1} xs={12} alignItems='center' justify='flex-end'>
                      <Grid item>
                        {programBasicInfo ? (
                          <TabletButton variant='text' onClick={handleReset} disabled={fieldsUnchanged}>
                            Discard Changes
                          </TabletButton>
                        ) : null}
                      </Grid>
                      <Grid item>
                        <TabletButton
                          size='medium'
                          variant='contained'
                          color='primary'
                          type='submit'
                          disabled={saveProgramIsLoading || fieldsUnchanged}
                        >
                          Save Basic Info
                        </TabletButton>
                      </Grid>
                    </Grid>
                  )}
                </Grid>

                {showConfirmationDialog ? (
                  <ConfirmationDialog
                    title='Allow volunteer to automatically join this program?'
                    body={
                      <>
                        <Typography variant='body2' gutterBottom>
                          Volunteers will be able to automatically join this program and current applications will be
                          approved.
                        </Typography>
                      </>
                    }
                    open
                    closeActionText='Cancel'
                    confirmActionText='Confirm'
                    handleCloseClick={() => setShowConfirmationDialog(false)}
                    handleConfirmClick={() => {
                      setFieldValue('isRestrictedProgram', !values.isRestrictedProgram);
                      setShowConfirmationDialog(false);
                    }}
                  />
                ) : null}

                {showAutoEnrolDialog ? (
                  <ConfirmationDialog
                    title='Enrol every volunteer in this program?'
                    body={
                      <>
                        <Typography variant='body2' gutterBottom>
                          Once you save, every active volunteer in your organisation will be enrolled in this program,
                          and new volunteers will be enrolled automatically as they join.
                        </Typography>
                        <Typography variant='body2' gutterBottom>
                          Volunteers are not notified, but they will be added to any chat channels linked to this
                          program. Turning this off later does not un-enrol anyone.
                        </Typography>
                      </>
                    }
                    open
                    closeActionText='Cancel'
                    confirmActionText='Confirm'
                    handleCloseClick={() => setShowAutoEnrolDialog(false)}
                    handleConfirmClick={() => {
                      setFieldValue('autoEnrolAllVolunteers', true);
                      setShowAutoEnrolDialog(false);
                    }}
                  />
                ) : null}
              </Form>
            );
          }}
        </Formik>
      </FormSectionV2>
    );
  }
);
